import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";
import { LETTER_WORDS } from "@/utils/quizData";

import { getVoiceLang } from "@/utils/voicePref";
// Build flat letter list — picks first word per letter for explore order
const LETTERS = Object.entries(LETTER_WORDS).map(([l, words]) => ({
  l, word: words[0].w, emoji: words[0].e, allWords: words,
}));

// Pick a random word variation for a letter (each call may return different word)
const randomLetterEntry = (letter) => {
  const meta = LETTER_WORDS[letter.l] || letter.allWords;
  const pool = meta || [{ w: letter.word, e: letter.emoji }];
  const pick = pool[Math.floor(Math.random() * pool.length)];
  return { l: letter.l, word: pick.w, emoji: pick.e };
};

const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const BelajarHuruf = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [mode, setMode] = useState("explore"); // explore | quiz
  const [letterIdx, setLetterIdx] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quizRound, setQuizRound] = useState(0);
  const [quizTarget, setQuizTarget] = useState(null);
  const [quizOpts, setQuizOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const speakLetter = (letter) => {
    if (!soundOn) return;
    speakText(`${letter.l}, ${letter.word}`);
  };

  const goPrev = () => { const i = (letterIdx - 1 + LETTERS.length) % LETTERS.length; setLetterIdx(i); speakLetter(LETTERS[i]); };
  const goNext = () => { const i = (letterIdx + 1) % LETTERS.length; setLetterIdx(i); speakLetter(LETTERS[i]); };

  const newQuiz = useCallback(() => {
    const correctLetter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
    const correct = randomLetterEntry(correctLetter);
    const wrongs = [];
    while (wrongs.length < 3) {
      const w = LETTERS[Math.floor(Math.random() * LETTERS.length)];
      if (w.l !== correct.l && !wrongs.find(x => x.l === w.l)) wrongs.push(randomLetterEntry(w));
    }
    const opts = [...wrongs, correct].sort(() => Math.random() - 0.5);
    setQuizTarget(correct); setQuizOpts(opts); setFeedback(null);
    setTimeout(() => { if (soundOn) speakText(`Which letter is ${correct.l}? ${correct.l} is for ${correct.word}`); }, 300);
  }, [soundOn]);

  useEffect(() => { if (mode === "quiz") newQuiz(); }, [mode, newQuiz]);

  const handleQuiz = (l) => {
    if (feedback) return;
    if (l.l === quizTarget.l) {
      setFeedback({ type: "ok", text: `Benar! ${l.l}!` });
      setQuizScore(s => s + 1);
      if (soundOn) speakText(`Correct! ${l.l} is for ${l.word}`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${l.l}` });
      if (soundOn) speakText(`The correct answer is ${quizTarget.l}`);
    }
    setTimeout(() => {
      if (quizRound + 1 >= totalRounds) { setWon(true); recordGameWon("huruf"); }
      else { setQuizRound(r => r + 1); newQuiz(); }
    }, 1500);
  };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="huruf-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">📚</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {quizScore}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setQuizScore(0); setQuizRound(0); setWon(false); newQuiz(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={() => setMode("explore")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Belajar Lagi</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (mode === "quiz") {
    return (
      <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="huruf-quiz">
        <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
          <span className="font-fredoka text-gold font-bold text-sm">📚 {quizRound + 1}/{totalRounds} • Skor: {quizScore}</span>
          <div className="flex gap-1.5">
            <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
            <Button onClick={() => setMode("explore")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2 text-xs">Eksplor</Button>
            <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
          </div>
        </div>
        <motion.div key={quizRound} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
          <p className="font-fredoka text-gold-light/70 text-sm mb-1">Klik huruf untuk:</p>
          <span className="text-7xl block mb-1">{quizTarget?.emoji}</span>
          <p className="font-fredoka text-2xl text-gold font-bold">{quizTarget?.word}</p>
        </motion.div>
        <AnimatePresence>
          {feedback && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`huruf-feedback-${feedback.type}`}>
              <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
                {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="grid grid-cols-2 gap-4 w-full">
          {quizOpts.map((l, i) => (
            <motion.button key={`${l.l}-${i}`} data-testid={`huruf-opt-${i}`}
              onClick={() => handleQuiz(l)} disabled={!!feedback}
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
              className="ingredient-3d quiz-option-btn-large text-center text-slate-800 font-fredoka font-bold flex items-center justify-center">
              {l.l}
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // Explore mode
  const cur = LETTERS[letterIdx];
  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="huruf-explore">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">📚 Huruf {letterIdx + 1}/26</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setMode("quiz")} className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-fredoka font-bold rounded-lg h-7 text-xs px-3" data-testid="huruf-quiz-btn">Mulai Kuis</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <motion.div key={letterIdx} initial={{ scale: 0.7, opacity: 0, rotateY: -90 }} animate={{ scale: 1, opacity: 1, rotateY: 0 }}
        className="glass-card-strong rounded-3xl p-8 w-full text-center mb-3" onClick={() => speakLetter(cur)} data-testid="huruf-card">
        <p className="font-fredoka text-9xl sm:text-[10rem] text-gold font-black drop-shadow-2xl mb-2">{cur.l}</p>
        <span className="text-7xl block mb-2">{cur.emoji}</span>
        <p className="font-fredoka text-2xl text-gold-light font-bold">{cur.l} untuk {cur.word}</p>
      </motion.div>

      <div className="flex gap-3 w-full">
        <Button data-testid="huruf-prev" onClick={goPrev} className="flex-1 glass-card border-gold/20 text-gold font-fredoka font-bold rounded-xl h-12">⬅ Sebelumnya</Button>
        <Button data-testid="huruf-speak" onClick={() => speakLetter(cur)} className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-fredoka font-bold rounded-xl h-12 px-4"><Volume2 className="w-4 h-4" /></Button>
        <Button data-testid="huruf-next" onClick={goNext} className="flex-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-fredoka font-bold rounded-xl h-12">Berikutnya ➡</Button>
      </div>
    </div>
  );
};

export default BelajarHuruf;
